import Activity from '@/models/_deprecated/Activity';
import { CodeEditorActivityElement } from '@/models/_deprecated/ActivityElement';
import {
  CodeEditorActivity,
  CodeEditorTestCase,
} from '@/models/CodeEditorActivity';
import { ApiError } from '@/models/Errors';
import { useEffect, useState } from 'react';
import CodeEditorForm, {
  CodeEditorFormFields,
  PartialCodeEditorFormFields,
  TestCaseFields,
} from './CodeEditorForm';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { useMutation } from '@tanstack/react-query';
import {
  createCodeEditorActivity,
  updateCodeEditorActivity,
  UpdateCodeEditorActivity,
  validateCodeEditorActivity,
} from '@/data/services/activityElement/codeEditorActivityServices';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { useTranslation } from 'react-i18next';
import useListService from '@/data/hook/useListService';
import { codeEditorTestCasesQueryKeys } from '@/data/services/querykeys';
import {
  createCodeEditorTestCase,
  deleteCodeEditorTestCase,
  updateCodeEditorTestCase,
} from '@/data/services/activityElement/codeEditorTestCaseServices';
import { executeCode } from '@/data/services/codeExecutionService';
import { LoadingIcon } from '@/components/icons';
import toast from 'react-hot-toast/headless';

type onEditFn = (question: Partial<CodeEditorActivityElement>) => void;

type CodeEditorFieldsKeys = keyof CodeEditorFormFields;

type ElementWithTestCases = CodeEditorActivityElement & {
  testCases: CodeEditorTestCase[];
};

type CodeEditorFormProps = {
  error?: ApiError<CodeEditorFieldsKeys>;
  question: CodeEditorActivityElement | ElementWithTestCases;
  activity: Activity;
  onEdit: onEditFn;
  onSave: (question: CodeEditorActivity) => void;
};

export default function CodeEditorService({
  question,
  activity,
  onEdit,
  onSave,
}: CodeEditorFormProps) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const [tCodeEditorForm] = useTranslation('translation', {
    keyPrefix: 'codeEditorForm',
  });

  const [defaultValues, setDefaultValues] = useState<CodeEditorFormFields>({
    testCases: [],
    ...question,
  });
  const codeEditorActivityId = question.id;
  const { results: testCases, invalidate } = useListService({
    ...codeEditorTestCasesQueryKeys.list({ codeEditorActivityId }),
    enabled: !!question.id && !isNaN(question.id),
  });
  const isGenerated = question.changeStatus === 'new' && !!question.title;

  const onError = (error: unknown) => {
    alert.error(getErrorMessage(error));
  };

  type OnSuccessParams = {
    codeEditorActivity: CodeEditorActivity;
    testCases: CodeEditorTestCase[];
  };

  const onSuccess = async ({
    codeEditorActivity,
    testCases,
  }: OnSuccessParams) => {
    onSave(codeEditorActivity);
    alert.success(t('saved'));
    setDefaultValues({ ...codeEditorActivity, testCases });
    return invalidate();
  };

  const { mutateAsync: create, isLoading: loadingCreate } = useMutation(
    handleCreate,
    {
      onSuccess,
      onError,
    },
  );

  async function handleUpdate({
    changes,
    codeActivityId,
  }: UpdateCodeEditorActivity): Promise<OnSuccessParams> {
    const payload = buildChangedObject(defaultValues, changes);

    if (!Object.values(payload).length)
      return { codeEditorActivity: question, testCases };

    const hasTestCases = 'testCases' in payload;
    delete payload.testCases;

    const groupedTestCases = hasTestCases
      ? groupByOperation(changes.testCases ?? [], defaultValues.testCases ?? [])
      : undefined;

    const [codeEditorActivity, createdTestCases, updatedTestCases] =
      await Promise.all([
        updateCodeEditorActivity(codeActivityId, payload),
        createTestCases(codeActivityId, groupedTestCases?.toCreate ?? []),
        updateTestCases(groupedTestCases?.toUpdate ?? []),
        deleteTestCases(groupedTestCases?.toDelete ?? []),
      ]);

    const testCasesList = hasTestCases
      ? [...createdTestCases, ...updatedTestCases].sort((a, b) => a.id - b.id)
      : testCases;

    return { codeEditorActivity, testCases: testCasesList };
  }

  async function deleteTestCases(testCases: Required<TestCaseFields>[]) {
    await Promise.all(testCases.map(({ id }) => deleteCodeEditorTestCase(id)));
  }

  const { mutateAsync: update, isLoading: loadingUpdate } = useMutation(
    handleUpdate,
    {
      onSuccess,
      onError,
    },
  );

  const {
    mutateAsync: runTestCases,
    isLoading: isValidating,
    data: validationResults,
  } = useMutation(executeCode);

  const onValidate = async (data: Partial<CodeEditorFormFields>) => {
    const alertId = alert.warning(tCodeEditorForm('validating'), {
      icon: <LoadingIcon className="w-6 h-6" />,
    });
    const result = await runTestCases({
      solutionCode: data.solutionCode ?? '',
      language: data.language ?? '',
      testCases: data.testCases || testCases || [],
    });
    toast.remove(alertId);
    return result;
  };

  function onChange(data: PartialCodeEditorFormFields) {
    onEdit({
      ...data,
      changeStatus: question.changeStatus,
      id: question.id,
      identifier: question.identifier,
    });
  }

  function createTestCases(
    codeEditorActivityId: number,
    testCases: TestCaseFields[],
  ) {
    const testCaseRequests = testCases.map(testCase =>
      createCodeEditorTestCase({ ...testCase, codeEditorActivityId }),
    );
    return Promise.all(testCaseRequests);
  }

  async function handleCreate(data: Partial<CodeEditorFormFields>) {
    const testCasesFields = data.testCases || [];
    delete data.testCases;

    const codeEditorActivity = await createCodeEditorActivity({
      ...question,
      ...data,
      activity: activity.id,
    });
    const testCases = await createTestCases(
      codeEditorActivity.id,
      testCasesFields,
    );

    return { codeEditorActivity, testCases };
  }

  function updateTestCases(testCases: Required<TestCaseFields>[]) {
    return Promise.all(testCases.map(handleUpdateTestCases));

    async function handleUpdateTestCases(
      testCase: Required<TestCaseFields>,
    ): Promise<CodeEditorTestCase> {
      const defaultTestCase = defaultValues.testCases?.find(
        formTestCase => formTestCase.id === testCase.id,
      );

      if (!defaultTestCase) throw new Error('Test case not found');

      const changedFields = buildChangedObject(defaultTestCase, testCase);

      if (!Object.values(changedFields).length)
        return {
          ...testCase,
          codeEditorActivityId: question.id,
        };

      return updateCodeEditorTestCase(testCase.id, changedFields);
    }
  }

  async function runValidateBeforeSave(changes: Partial<CodeEditorFormFields>) {
    if (changes.testCases?.length) {
      const { isValidated } = await onValidate(changes);
      return isValidated;
    }
    return true;
  }

  const onSubmit = async (changes: Partial<CodeEditorFormFields>) => {
    const isValidated = await runValidateBeforeSave(changes);

    if (!isValidated) {
      alert.error(tCodeEditorForm('invalidCode'), { className: 'z-50' });
      return;
    }

    let activityId = question.id;
    if (question.changeStatus === 'new') {
      const {
        codeEditorActivity: { id },
      } = await create(changes);
      activityId = id;
    } else {
      await update({ codeActivityId: question.id, changes });
    }
    await validateCodeEditorActivity(activityId);
  };

  useEffect(() => {
    if (!testCases.length) return;
    setDefaultValues(defaultValues => ({
      ...defaultValues,
      testCases,
    }));
  }, [testCases]);

  const isSubmitting = loadingCreate || loadingUpdate;

  return (
    <CodeEditorForm
      key={JSON.stringify(defaultValues)}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      onChange={onChange}
      onValidate={onValidate}
      isSubmitting={isSubmitting}
      validation={{
        isValidating,
        results: validationResults?.executionResults,
      }}
      startsEnabled={isGenerated}
    />
  );
}

type GroupedTestCases = {
  toUpdate: Required<TestCaseFields>[];
  toCreate: Omit<TestCaseFields, 'id'>[];
  toDelete: Required<TestCaseFields>[];
};

function groupByOperation(
  testCases: TestCaseFields[],
  defaultValues: TestCaseFields[],
): GroupedTestCases {
  const groupedTestCases: GroupedTestCases = {
    toUpdate: [],
    toCreate: [],
    toDelete: [],
  };

  for (const testCase of testCases) {
    if (testCase.id === undefined) {
      groupedTestCases.toCreate.push(testCase);
    } else {
      groupedTestCases.toUpdate.push({ ...testCase, id: testCase.id });
    }
  }
  groupedTestCases.toDelete = defaultValues.filter(
    testCase => !groupedTestCases.toUpdate.some(t => t.id === testCase.id),
  ) as Required<TestCaseFields>[];
  return groupedTestCases;
}
